import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import PricingPage from "@/pages/PricingPage";
import { motion, useScroll, useTransform, useInView, AnimatePresence } from "framer-motion";
import {
  ArrowRight, Sparkles, MessageSquare, Zap, Shield, Bot, Globe, Users,
  ChevronDown, ChevronUp, Pen, Copy, Check, Star, Clock, Lock, Send,
  Heart, Briefcase, HandHeart, Laugh, Languages, BookOpen, Layers, Mic, Wrench, Puzzle
} from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const DEMO_TEXT = "hey i was wondering if maybe you could possibly help me with something? sorry to bother you but i think there might be an issue with the project and i was just wondering if you had time to look at it. no pressure tho lol";
const DEMO_RESULTS = {
  confident: "I need your help with a project issue. Can you take a look at it when you get a chance?",
  apologize: "I owe you an honest heads-up — there's an issue with the project I should have caught sooner. I'd really appreciate your help sorting it out, and I'll make sure it doesn't happen again.",
  flirty: "So I may have gotten myself into a tiny situation with our project... any chance my favorite problem-solver has a few minutes to save the day?",
  professional: "I'd like to bring a project issue to your attention. When your schedule permits, could we review the matter together? I've identified the concern and would value your input on the resolution.",
};

/* ── Scroll reveal wrapper ── */
function Reveal({ children, delay = 0, className = "" }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-60px" });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 32 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

/* ── Staggered text ── */
function StaggerText({ text, className = "", tag = "span" }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-40px" });
  const words = text.split(" ");
  const Tag = tag;
  return (
    <Tag ref={ref} className={className}>
      {words.map((word, i) => (
        <motion.span
          key={i}
          initial={{ opacity: 0, y: 16 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5, delay: i * 0.04, ease: [0.22, 1, 0.36, 1] }}
          style={{ display: "inline-block", marginRight: "0.25em" }}
        >
          {word}
        </motion.span>
      ))}
    </Tag>
  );
}

/* ── Cursor glow ── */
function CursorGlow() {
  const [pos, setPos] = useState({ x: -200, y: -200 });
  useEffect(() => {
    const handler = (e) => setPos({ x: e.clientX, y: e.clientY });
    window.addEventListener("mousemove", handler);
    return () => window.removeEventListener("mousemove", handler);
  }, []);
  return (
    <div
      className="pointer-events-none fixed z-[9999] hidden md:block"
      style={{
        left: pos.x - 120, top: pos.y - 120,
        width: 240, height: 240,
        background: "radial-gradient(circle, rgba(0,96,57,0.06) 0%, transparent 70%)",
        borderRadius: "50%",
        transition: "left 0.15s ease-out, top 0.15s ease-out",
      }}
    />
  );
}

/* ── Magnetic button ── */
function MagneticButton({ children, className = "", onClick, style = {}, ...props }) {
  const ref = useRef(null);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const handleMove = (e) => {
    if (!ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    setOffset({ x: (e.clientX - cx) * 0.15, y: (e.clientY - cy) * 0.15 });
  };
  return (
    <motion.button
      ref={ref}
      onMouseMove={handleMove}
      onMouseLeave={() => setOffset({ x: 0, y: 0 })}
      animate={{ x: offset.x, y: offset.y }}
      transition={{ type: "spring", stiffness: 300, damping: 20 }}
      className={className}
      onClick={onClick}
      style={style}
      {...props}
    >
      {children}
    </motion.button>
  );
}

export default function LandingPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [demoIntent, setDemoIntent] = useState("confident");
  const [demoText, setDemoText] = useState("");
  const [demoResult, setDemoResult] = useState("");
  const [demoLoading, setDemoLoading] = useState(false);
  const [demoCopied, setDemoCopied] = useState(false);
  const [demoLimitReached, setDemoLimitReached] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);

  const { scrollYProgress } = useScroll();
  const heroParallax = useTransform(scrollYProgress, [0, 0.3], [0, -50]);

  const handleDemo = async (intentKey) => {
    if (demoLimitReached) { navigate("/login"); return; }
    setDemoIntent(intentKey);
    setDemoLoading(true);
    setDemoResult("");
    // Signed-in users hit the REAL /api/rewrite endpoint (unlimited for Pro,
    // 10/day for Free — enforced by backend). Anonymous visitors fall back to
    // the demo endpoint with its IP-based 10/day cap.
    const token = localStorage.getItem("buddy_access_token");
    try {
      if (token) {
        const { data } = await axios.post(
          `${BACKEND_URL}/api/rewrite`,
          { text: demoText || DEMO_TEXT, intent: intentKey, platform: "email", mode: "rewrite" },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setDemoResult(data.rewritten_text);
      } else {
        const { data } = await axios.post(`${BACKEND_URL}/api/demo/rewrite`, {
          text: demoText || DEMO_TEXT,
          intent: intentKey,
          platform: "email",
          mode: "rewrite",
        });
        setDemoResult(data.rewritten_text);
      }
    } catch (err) {
      // Anonymous demo cap → show "Sign up free" CTA.
      // Signed-in daily cap → LimitReachedModal already fires globally via the
      // axios 402 interceptor, so we just clear the result cleanly.
      if (!token && err.response?.status === 429) {
        setDemoLimitReached(true);
        setDemoResult("");
      } else if (token && err.response?.status === 402) {
        setDemoResult("");
      } else {
        setDemoResult(DEMO_RESULTS[intentKey] || DEMO_RESULTS.confident);
      }
    } finally {
      setDemoLoading(false);
    }
  };

  const demoIntents = [
    { key: "confident", label: "Confident", icon: Zap, color: "#006039" },
    { key: "apologize", label: "Apologize", icon: Shield, color: "#A67C52" },
    { key: "flirty", label: "Flirty", icon: Laugh, color: "#8B3A4A" },
    { key: "professional", label: "Professional", icon: Briefcase, color: "#3A3A38" },
  ];

  const features = [
    { icon: Sparkles, title: "Emotional Intelligence", desc: "11 emotional tones that transform how your message lands. From confident to flirty — always authentic." },
    { icon: Globe, title: "Platform-Aware", desc: "Buddy knows WhatsApp is casual and LinkedIn is polished. Your tone adapts to where you're sending it." },
    { icon: Bot, title: "Humanize AI Text", desc: "Paste any AI-generated text and Buddy makes it sound like a real human wrote it. No more robot vibes." },
    { icon: Wrench, title: "AI Tools Suite", desc: "Resume Checker, CaptionCraft, Summarizer, Plagiarism, Citation, Cover Letter — six pro tools, one workspace." },
    { icon: Mic, title: "Voice-to-Text + AI Rewrite", desc: "Speak your draft, Buddy transcribes and rewrites it in your chosen tone. Fastest way from thought to polished message." },
    { icon: Layers, title: "3 Tone Variations", desc: "See your message rewritten three ways — Light, Balanced, and Strong — and pick the one that fits." },
    { icon: Languages, title: "Translate & Improve", desc: "Translate into 20+ languages while maintaining your intended emotional tone. Natural, not robotic." },
    { icon: BookOpen, title: "Saved Library", desc: "Save your best rewrites to a personal library. Find and reuse your perfect messages anytime." },
  ];

  const faqs = [
    { q: "How is Buddy different from other writing tools?", a: "Buddy doesn't just fix grammar — it rewrites your message in the exact emotional tone you want, optimized for the platform you're using. It's like having a communication coach who understands context, emotion, and audience." },
    { q: "Will my messages sound like AI wrote them?", a: "The opposite. Buddy's entire purpose is making text sound naturally human. Our 'Humanize' mode specifically removes all AI-sounding phrases and patterns." },
    { q: "Is my data safe?", a: "Your messages are processed in real-time and never stored for AI training. We use enterprise-grade encryption and you can delete your history anytime." },
    { q: "Can I try before I pay?", a: "Yes! The free plan gives you 10 rewrites per day with all tones and platforms. No credit card required." },
    { q: "What languages does Buddy support?", a: "Buddy can rewrite in English and translate to 20+ languages on the Pro plan. Free users get a generous daily quota to try every tone and platform." },
    { q: "Can my whole team use Buddy?", a: "Yes! The Teams plan includes brand tone presets, shared templates, and a team admin dashboard to keep everyone on-brand." },
  ];

  return (
    <div className="min-h-screen overflow-x-hidden" style={{ background: "var(--surface-cream)" }}>
      <CursorGlow />

      {/* ─── Navigation ─── */}
      <nav className="sticky top-0 z-50" style={{ background: "rgba(250,250,248,0.8)", backdropFilter: "blur(16px)", WebkitBackdropFilter: "blur(16px)", borderBottom: "1px solid rgba(232,228,220,0.6)" }}>
        <div className="max-w-6xl mx-auto px-4 h-16 flex items-center justify-between">
          <motion.h1
            className="font-display text-3xl font-bold cursor-pointer"
            style={{ color: "var(--rolex-green)" }}
            onClick={() => navigate("/")}
            whileHover={{ scale: 1.03 }}
            data-testid="landing-logo"
          >
            Buddy
          </motion.h1>
          <div className="hidden md:flex items-center gap-8">
            <a href="#features" className="text-sm font-medium transition-colors duration-300 hover:text-[var(--rolex-green)]" style={{ color: "var(--text-secondary)" }}>Features</a>
            <a href="#pricing" className="text-sm font-medium transition-colors duration-300 hover:text-[var(--rolex-green)]" style={{ color: "var(--text-secondary)" }}>Pricing</a>
            <button onClick={() => navigate("/downloads")} className="text-sm font-medium transition-colors duration-300 hover:text-[var(--rolex-green)]" style={{ color: "var(--text-secondary)", background: "none", border: "none", cursor: "pointer" }}>Downloads</button>
            <button onClick={() => navigate("/ai-tools")} className="text-sm font-medium transition-colors duration-300 hover:text-[var(--rolex-green)]" style={{ color: "var(--text-secondary)", background: "none", border: "none", cursor: "pointer" }}>AI Tools</button>
          </div>
          <div className="flex items-center gap-3">
            {user ? (
              <MagneticButton onClick={() => navigate("/app")} className="btn-primary py-2 px-5 text-sm" data-testid="landing-go-dashboard">Dashboard</MagneticButton>
            ) : (
              <>
                <button onClick={() => navigate("/login")} className="text-sm font-medium px-4 py-2 transition-all duration-300 hover:text-[var(--rolex-green)]" style={{ color: "var(--text-secondary)" }} data-testid="landing-login-btn">Sign In</button>
                <MagneticButton onClick={() => navigate("/login")} className="btn-primary py-2 px-5 text-sm" data-testid="landing-cta-btn">Try Free</MagneticButton>
              </>
            )}
          </div>
        </div>
      </nav>

      {/* ─── Hero ─── */}
      <section className="relative max-w-5xl mx-auto px-4 pt-24 pb-20 text-center">
        <motion.div style={{ y: heroParallax }}>
          <Reveal>
            <motion.div
              className="inline-flex items-center gap-2 px-5 py-2 rounded-full mb-8"
              style={{ background: "rgba(0,96,57,0.08)", color: "var(--rolex-green)", backdropFilter: "blur(8px)" }}
              whileHover={{ scale: 1.04 }}
            >
              <Sparkles size={14} />
              <span className="text-xs font-semibold tracking-wider uppercase">AI-Powered Communication</span>
            </motion.div>
          </Reveal>

          <Reveal delay={0.1}>
            <h1 className="font-display text-4xl md:text-5xl lg:text-[3.5rem] font-bold leading-[1.1]" style={{ color: "var(--text-primary)", letterSpacing: "-0.025em" }} data-testid="hero-heading">
              <StaggerText text="Say it, The way" tag="span" className="block" />
              <StaggerText text="you mean it" tag="span" className="block" />
            </h1>
          </Reveal>

          <Reveal delay={0.25}>
            <p className="max-w-2xl mx-auto mt-6 text-lg leading-relaxed" style={{ color: "var(--text-secondary)" }}>
              Whatever you want to say, Buddy helps you say it right.<br />
              Perfect tone, clear meaning, and confident delivery-every time.
            </p>
          </Reveal>

          <Reveal delay={0.35}>
            <div className="flex items-center justify-center gap-4 mt-10">
              <MagneticButton
                onClick={() => navigate(user ? "/app" : "/login")}
                className="btn-primary py-3.5 px-8 text-base flex items-center gap-2"
                data-testid="hero-cta-btn"
              >
                Start Writing Better <ArrowRight size={18} />
              </MagneticButton>
              <MagneticButton
                onClick={() => document.getElementById("demo")?.scrollIntoView({ behavior: "smooth" })}
                className="btn-secondary py-3.5 px-8 text-base"
                data-testid="hero-demo-btn"
              >
                See It In Action
              </MagneticButton>
            </div>
            <p className="mt-5 text-xs" style={{ color: "var(--text-light)" }}>Free forever. No credit card required.</p>
          </Reveal>
        </motion.div>

        {/* Soft gradient separator */}
        <div className="absolute bottom-0 left-0 right-0 h-24" style={{ background: "linear-gradient(to bottom, transparent, var(--surface-cream))" }} />
      </section>

      {/* ─── Live Demo ─── */}
      <section id="demo" className="max-w-4xl mx-auto px-4 pb-24">
        <Reveal>
          <div className="text-center mb-8">
            <span className="label-caps" style={{ color: "var(--rolex-gold)" }}>Live Demo</span>
            <h2 className="font-display text-2xl md:text-3xl font-semibold mt-2" style={{ color: "var(--text-primary)" }}>
              Same Message, Four Different Tones
            </h2>
            <p className="text-sm mt-2" style={{ color: "var(--text-muted)" }}>Pick a tone or edit the message to see real-time magic.</p>
          </div>
        </Reveal>
        <Reveal delay={0.15}>
          <motion.div
            className="p-6 md:p-8 rounded-2xl"
            style={{ background: "rgba(255,255,255,0.7)", backdropFilter: "blur(16px)", border: "1px solid rgba(232,228,220,0.6)", boxShadow: "0 8px 32px rgba(0,0,0,0.06)" }}
          >
            <div className="mb-5">
              <span className="label-caps block mb-2">Original Message</span>
              <div className="p-4 rounded-xl" style={{ background: "var(--surface-warm)" }}>
                <textarea
                  className="w-full bg-transparent border-none outline-none resize-none text-sm leading-relaxed"
                  style={{ color: "var(--text-secondary)" }}
                  rows={3}
                  value={demoText}
                  onChange={(e) => setDemoText(e.target.value)}
                  placeholder={DEMO_TEXT}
                  data-testid="demo-input"
                />
              </div>
            </div>
            <div className="flex flex-wrap gap-2 mb-5">
              {demoIntents.map(({ key, label, icon: Icon, color }) => (
                <motion.button
                  key={key}
                  onClick={() => handleDemo(key)}
                  className="intent-chip"
                  whileHover={{ scale: 1.04, y: -2 }}
                  whileTap={{ scale: 0.97 }}
                  style={demoIntent === key && demoResult
                    ? { background: color, color: "white", borderColor: color }
                    : { background: "var(--surface-white)", color, borderColor: color + "40" }
                  }
                  data-testid={`demo-intent-${key}`}
                >
                  <Icon size={14} />
                  {label}
                </motion.button>
              ))}
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="label-caps">Buddy's Rewrite</span>
                {demoResult && (
                  <button
                    onClick={() => { try { navigator.clipboard.writeText(demoResult); } catch {} setDemoCopied(true); setTimeout(() => setDemoCopied(false), 2000); }}
                    className="text-xs flex items-center gap-1 px-2 py-1 rounded"
                    style={{ color: demoCopied ? "var(--success)" : "var(--rolex-green)" }}
                    data-testid="demo-copy-btn"
                  >
                    {demoCopied ? <Check size={12} /> : <Copy size={12} />}
                    {demoCopied ? "Copied" : "Copy"}
                  </button>
                )}
              </div>
              <div className="p-4 rounded-xl min-h-[80px]" style={{ background: "rgba(0,96,57,0.06)" }}>
                {demoLimitReached ? (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="flex flex-col items-start gap-3"
                    data-testid="demo-limit-reached"
                  >
                    <p className="text-sm font-medium" style={{ color: "var(--rolex-green-dark)" }}>
                      You've tried Buddy a few times — we love that. Sign up free for more!
                    </p>
                    <button
                      onClick={() => navigate("/login")}
                      className="btn-primary py-2 px-5 text-xs flex items-center gap-1.5"
                      data-testid="demo-signup-btn"
                    >
                      Sign up free <ArrowRight size={13} />
                    </button>
                  </motion.div>
                ) : demoLoading ? (
                  <div className="space-y-2">
                    <div className="h-3.5 rounded animate-shimmer" style={{ width: "85%" }} />
                    <div className="h-3.5 rounded animate-shimmer" style={{ width: "65%" }} />
                  </div>
                ) : demoResult ? (
                  <motion.p
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="text-sm leading-relaxed"
                    style={{ color: "var(--rolex-green-dark)" }}
                    data-testid="demo-result"
                  >
                    {demoResult}
                  </motion.p>
                ) : (
                  <p className="text-sm" style={{ color: "var(--text-light)" }}>Click a tone above to see the rewrite</p>
                )}
              </div>
            </div>
          </motion.div>
        </Reveal>
      </section>

      {/* ─── How It Works ─── */}
      <section className="relative py-24" style={{ background: "var(--surface-white)" }}>
        <div className="absolute top-0 left-0 right-0 h-20" style={{ background: "linear-gradient(to bottom, var(--surface-cream), var(--surface-white))" }} />
        <div className="max-w-5xl mx-auto px-4 text-center">
          <Reveal>
            <span className="label-caps" style={{ color: "var(--rolex-gold)" }}>How It Works</span>
            <h2 className="font-display text-2xl md:text-3xl font-semibold mt-2 mb-14" style={{ color: "var(--text-primary)" }}>
              Three Steps to Perfect Communication
            </h2>
          </Reveal>
          <div className="grid md:grid-cols-3 gap-10">
            {[
              { step: "01", title: "Paste Your Message", desc: "Drop in any text — a rough draft, a received message, or even AI-generated content you want to humanize.", icon: Pen },
              { step: "02", title: "Pick Your Tone", desc: "Choose from 11 emotional presets and 5 platform styles. Buddy understands context the way you do.", icon: Sparkles },
              { step: "03", title: "Copy & Send", desc: "Get your rewritten message instantly. One click to copy. It reads like you wrote it at your absolute best.", icon: Send },
            ].map((item, i) => (
              <Reveal key={i} delay={i * 0.12}>
                <motion.div
                  className="text-center"
                  whileHover={{ y: -6, transition: { duration: 0.3 } }}
                >
                  <motion.div
                    className="w-16 h-16 rounded-2xl mx-auto mb-5 flex items-center justify-center"
                    style={{ background: "rgba(0,96,57,0.08)" }}
                    whileHover={{ scale: 1.08, boxShadow: "0 8px 24px rgba(0,96,57,0.12)" }}
                    transition={{ type: "spring", stiffness: 400 }}
                  >
                    <item.icon size={28} style={{ color: "var(--rolex-green)" }} />
                  </motion.div>
                  <span className="text-xs font-bold" style={{ color: "var(--rolex-gold)" }}>STEP {item.step}</span>
                  <h3 className="font-display text-lg font-semibold mt-2" style={{ color: "var(--text-primary)" }}>{item.title}</h3>
                  <p className="text-sm mt-2 leading-relaxed" style={{ color: "var(--text-muted)" }}>{item.desc}</p>
                </motion.div>
              </Reveal>
            ))}
          </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-20" style={{ background: "linear-gradient(to bottom, var(--surface-white), var(--surface-cream))" }} />
      </section>

      {/* ─── Features ─── */}
      <section id="features" className="max-w-5xl mx-auto px-4 py-24">
        <Reveal>
          <div className="text-center mb-14">
            <span className="label-caps" style={{ color: "var(--rolex-gold)" }}>Why Choose Buddy</span>
            <h2 className="font-display text-2xl md:text-3xl font-semibold mt-2" style={{ color: "var(--text-primary)" }}>
              The Communication Edge You Deserve
            </h2>
            <p className="mt-3 text-base" style={{ color: "var(--text-muted)" }}>
              Every feature designed to make your words work harder
            </p>
          </div>
        </Reveal>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {features.map((f, i) => (
            <Reveal key={i} delay={i * 0.08}>
              <motion.div
                className="p-6 rounded-2xl"
                style={{ background: "rgba(255,255,255,0.65)", backdropFilter: "blur(12px)", border: "1px solid rgba(232,228,220,0.5)", boxShadow: "0 2px 8px rgba(0,0,0,0.03)" }}
                whileHover={{ y: -4, boxShadow: "0 12px 32px rgba(0,0,0,0.08)", transition: { duration: 0.3 } }}
              >
                <motion.div
                  className="w-10 h-10 rounded-xl flex items-center justify-center mb-4"
                  style={{ background: "rgba(0,96,57,0.08)" }}
                  whileHover={{ scale: 1.1 }}
                >
                  <f.icon size={20} style={{ color: "var(--rolex-green)" }} />
                </motion.div>
                <h3 className="text-base font-semibold mb-2" style={{ color: "var(--text-primary)" }}>{f.title}</h3>
                <p className="text-sm leading-relaxed" style={{ color: "var(--text-muted)" }}>{f.desc}</p>
              </motion.div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ─── The Buddy Difference ─── */}
      <section className="relative py-24" style={{ background: "var(--surface-white)" }}>
        <div className="absolute top-0 left-0 right-0 h-20" style={{ background: "linear-gradient(to bottom, var(--surface-cream), var(--surface-white))" }} />
        <div className="max-w-5xl mx-auto px-4 text-center">
          <Reveal>
            <span className="label-caps" style={{ color: "var(--rolex-gold)" }}>The Buddy Difference</span>
            <h2 className="font-display text-2xl md:text-3xl font-semibold mt-2 mb-4" style={{ color: "var(--text-primary)" }}>
              Not Just a Grammar Tool. A Communication Partner.
            </h2>
            <p className="max-w-2xl mx-auto text-base mb-14" style={{ color: "var(--text-muted)" }}>
              Other tools correct your spelling. Buddy transforms how people feel when they read your words.
            </p>
          </Reveal>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: Heart, color: "var(--rolex-gold-light)", iconColor: "var(--rolex-gold)", title: "Emotional Precision", desc: "11 carefully crafted emotional tones that go beyond 'formal' and 'casual.' Express confidence, empathy, charm, or sincerity — exactly when you need it." },
              { icon: Globe, color: "rgba(0,96,57,0.08)", iconColor: "var(--rolex-green)", title: "Context-Aware Writing", desc: "A LinkedIn post reads differently than a WhatsApp text. Buddy adjusts vocabulary, structure, and tone for each platform automatically." },
              { icon: Puzzle, color: "var(--info-bg)", iconColor: "var(--info)", title: "Browser Extension", desc: "Rewrite anywhere you type — Gmail, LinkedIn, WhatsApp Web, Twitter, docs and more. Live grammar and one-click tone changes without leaving the page." },
              { icon: Clock, color: "rgba(161,121,58,0.10)", iconColor: "var(--rolex-gold)", title: "Instant Results", desc: "Powered by frontier AI that delivers results in under a second. No waiting, no loading bars — just immediate, polished communication." },
            ].map((item, i) => (
              <Reveal key={i} delay={i * 0.1}>
                <motion.div
                  className="p-6 rounded-2xl text-left"
                  style={{ background: "rgba(255,255,255,0.65)", backdropFilter: "blur(12px)", border: "1px solid rgba(232,228,220,0.5)" }}
                  whileHover={{ y: -4, boxShadow: "0 12px 32px rgba(0,0,0,0.08)" }}
                >
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-4" style={{ background: item.color }}>
                    <item.icon size={20} style={{ color: item.iconColor }} />
                  </div>
                  <h3 className="text-base font-semibold mb-2" style={{ color: "var(--text-primary)" }}>{item.title}</h3>
                  <p className="text-sm leading-relaxed" style={{ color: "var(--text-muted)" }}>{item.desc}</p>
                </motion.div>
              </Reveal>
            ))}
          </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-20" style={{ background: "linear-gradient(to bottom, var(--surface-white), transparent)" }} />
      </section>

      {/* ─── Pricing ─── */}
      <section id="pricing" className="py-20" style={{ background: "var(--surface-cream)" }}>
        <PricingPage embedded />
      </section>

      {/* ─── FAQ ─── */}
      <section id="faq" className="relative py-20" style={{ background: "var(--surface-white)" }}>
        <div className="absolute top-0 left-0 right-0 h-20" style={{ background: "linear-gradient(to bottom, var(--surface-cream), var(--surface-white))" }} />
        <div className="max-w-3xl mx-auto px-4">
          <Reveal>
            <div className="text-center mb-10">
              <span className="label-caps" style={{ color: "var(--rolex-gold)" }}>FAQ</span>
              <h2 className="font-display text-2xl md:text-3xl font-semibold mt-2" style={{ color: "var(--text-primary)" }}>
                Questions? We've Got Answers.
              </h2>
            </div>
          </Reveal>
          <div className="space-y-3">
            {faqs.map((faq, i) => (
              <Reveal key={i} delay={i * 0.06}>
                <motion.div
                  className="rounded-2xl overflow-hidden"
                  style={{ background: "rgba(255,255,255,0.6)", backdropFilter: "blur(12px)", border: "1px solid rgba(232,228,220,0.5)" }}
                  whileHover={{ boxShadow: "0 4px 16px rgba(0,0,0,0.05)" }}
                  data-testid={`faq-item-${i}`}
                >
                  <button
                    onClick={() => setOpenFaq(openFaq === i ? null : i)}
                    className="w-full flex items-center justify-between p-5 text-left transition-colors"
                  >
                    <span className="text-sm font-medium pr-4" style={{ color: "var(--text-primary)" }}>{faq.q}</span>
                    <motion.div animate={{ rotate: openFaq === i ? 180 : 0 }} transition={{ duration: 0.3 }}>
                      <ChevronDown size={18} style={{ color: "var(--text-muted)" }} />
                    </motion.div>
                  </button>
                  <AnimatePresence>
                    {openFaq === i && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                        className="overflow-hidden"
                      >
                        <div className="px-5 pb-5">
                          <p className="text-sm leading-relaxed" style={{ color: "var(--text-muted)" }}>{faq.a}</p>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              </Reveal>
            ))}
          </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-20" style={{ background: "linear-gradient(to bottom, var(--surface-white), var(--surface-cream))" }} />
      </section>

      {/* ─── Final CTA ─── */}
      <section className="py-24" style={{ background: "var(--surface-cream)" }}>
        <div className="max-w-3xl mx-auto px-4 text-center">
          <Reveal>
            <h2 className="font-display text-3xl md:text-4xl font-bold" style={{ color: "var(--text-primary)" }}>
              <StaggerText text="Ready to Communicate" tag="span" className="block" />
              <StaggerText text="With Confidence?" tag="span" className="block" />
            </h2>
            <p className="mt-5 text-base" style={{ color: "var(--text-muted)" }}>
              Join thousands who write better, connect deeper, and get results with every message.
            </p>
          </Reveal>
          <Reveal delay={0.2}>
            <MagneticButton
              onClick={() => navigate(user ? "/app" : "/login")}
              className="btn-primary py-3.5 px-10 text-base mt-10 inline-flex items-center gap-2"
              data-testid="footer-cta-btn"
            >
              Start Writing Better — It's Free <ArrowRight size={18} />
            </MagneticButton>
          </Reveal>
        </div>
      </section>

      {/* ─── Footer ─── */}
      <footer className="py-10" style={{ borderTop: "1px solid rgba(232,228,220,0.5)", background: "var(--surface-white)" }}>
        <div className="max-w-6xl mx-auto px-4 flex flex-col md:flex-row items-center justify-between gap-4">
          <div>
            <h2 className="font-display text-2xl font-bold" style={{ color: "var(--rolex-green)" }}>Buddy</h2>
            <p className="text-xs mt-1" style={{ color: "var(--text-light)" }}>Communication, perfected.</p>
          </div>
          <div className="flex items-center gap-6">
            <a href="#features" className="text-xs transition-colors hover:text-[var(--rolex-green)]" style={{ color: "var(--text-muted)" }}>Features</a>
            <a href="#pricing" className="text-xs transition-colors hover:text-[var(--rolex-green)]" style={{ color: "var(--text-muted)" }}>Pricing</a>
            <button onClick={() => navigate("/downloads")} className="text-xs transition-colors hover:text-[var(--rolex-green)]" style={{ color: "var(--text-muted)", background: "none", border: "none", cursor: "pointer" }}>Downloads</button>
            <a href="#faq" className="text-xs transition-colors hover:text-[var(--rolex-green)]" style={{ color: "var(--text-muted)" }}>FAQ</a>
            <button
              onClick={() => window.dispatchEvent(new CustomEvent("buddy:open-feedback"))}
              className="text-xs font-semibold transition-opacity hover:opacity-80"
              style={{ color: "var(--rolex-green)", background: "none", border: "none", cursor: "pointer", padding: 0 }}
              data-testid="footer-feedback-btn"
            >
              Feedback
            </button>
          </div>
          <p className="text-xs" style={{ color: "var(--text-light)" }}>&copy; {new Date().getFullYear()} Buddy. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
